import React, { useState, useRef, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  ThemeProvider,
  createTheme,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ButtonGroup,
  Divider,
  Toolbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InfoIcon from '@mui/icons-material/Info';
import { generateCreditNotePDF } from "./pdfGenerator";
import { useUserContext } from './context/UserContext';
import { InvoiceContext } from './context/InvoiceContext';
import { doc, getDoc, updateDoc, increment, collection, addDoc, Timestamp, runTransaction } from "firebase/firestore";
import { db } from "../firebase.config";
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import {
  Create,
  Close,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";

const theme = createTheme({
  palette: {
    primary: { main: "#3f51b5" },
    secondary: { main: "#dc004e" },
  },
  typography: {
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    h4: { fontWeight: 700 },
    h6: { fontWeight: 600 },
  },
});

const formatCurrency = (amount, symbol = "₹") => {
  const num = parseFloat(amount);
  if (isNaN(num)) return `${symbol}0.00`;
  return `${symbol}${num.toFixed(2)}`;
};

// --- Main CreditNote Component ---
const CreditNote = () => {
  const {
    userProfile,
    invoiceLogo,
    invoiceBanner,
    invoiceBusinessName,
    invoiceBusinessAddress,
    userId,
    currency,
    invoiceFooter,
    invoiceIdPrefix,
    invoiceCustomCounter,
    invoiceDefaultAdditionalNotes,
    invoiceDefaultTermsAndConditions,
    invoiceDefaultAuthorizedSignature,
    invoiceDefaultAuthorizedSignatureName,
  } = useUserContext();

  const getCurrencySymbol = () => {
    if (currency === "INR") return "₹";
    if (currency === "USD") return "$";
    if (currency === "EUR") return "€";
    if (currency === "GBP") return "£";
    if (currency === "JPY") return "¥";
    return "₹";
  };
  const currencySymbol = getCurrencySymbol();

  const navigate = useNavigate();
  const contextData = useContext(InvoiceContext);
  const { selectedItems = [], setSelectedItems, invoiceDraft, setInvoiceDraft } = contextData || {};
  
  const [creditNoteNumber, setCreditNoteNumber] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [billTo, setBillTo] = useState({ name: "", address: "" });
  const [creditNoteDate, setCreditNoteDate] = useState(new Date().toISOString().split('T')[0]);
  const [items, setItems] = useState([
    { description: "", unitPrice: 0, quantity: 0, discount: 0, tax: 0, total: 0, isLocked: false, sourceId: null },
  ]);

  const [additionalNotes, setAdditionalNotes] = useState("");
  const [termsAndConditions, setTermsAndConditions] = useState('');
  
  const [isPrinting, setIsPrinting] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [creditNoteId, setCreditNoteId] = useState(null);

  const [authorizedSignature, setAuthorizedSignature] = useState("");
  const [authorizedSignatureName, setAuthorizedSignatureName] = useState("");
  const [authorizedSignatureImage, setAuthorizedSignatureImage] = useState(null);
  const [signatureErrors, setSignatureErrors] = useState({ authorized: "" });
  const [selectedCurrency, setSelectedCurrency] = useState(currency || "INR");

  const contentRef = useRef(null);

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' // 'success', 'error', 'warning', 'info'
  });

  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const handleCloseNotification = (event, reason) => {
    if (reason === 'clickaway') return;
    setNotification({ ...notification, open: false });
  };

  // Helper function to get financial year (FY format: 24-25, 25-26, etc.)
  const getFinancialYear = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth(); // 0-11, where 0=Jan, 3=Apr
    
    // Financial year starts from April (month 3)
    if (currentMonth >= 3) {
      // April onwards: current FY
      const startYear = currentYear % 100; // Last 2 digits
      const endYear = (currentYear + 1) % 100;
      return `${String(startYear).padStart(2, '0')}-${String(endYear).padStart(2, '0')}`;
    } else {
      // Jan to March: previous FY
      const endYear = currentYear % 100;
      const startYear = (currentYear - 1) % 100;
      return `${String(startYear).padStart(2, '0')}-${String(endYear).padStart(2, '0')}`;
    }
  };

  // Generate sequential reference number only when saving
  const generateSequentialReferenceNumber = async () => {
    try {
      const fy = getFinancialYear();
      const userDocRef = doc(db, "users", userId);
      let refNumber = `CN-${fy}-00001`;
      
      // Get current counter in a transaction
      await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userDocRef);
        
        if (userDoc.exists()) {
          const counter = (userDoc.data()?.creditNoteRefCounter || 0) + 1;
          if (counter > 99999) {
            throw new Error("Credit note reference counter exceeded maximum limit (99999)");
          }
          refNumber = `CN-${fy}-${String(counter).padStart(5, '0')}`;
          // Update counter to mark this number as used
          transaction.update(userDocRef, { creditNoteRefCounter: counter });
        } else {
          refNumber = `CN-${fy}-00001`;
          transaction.set(userDocRef, { creditNoteRefCounter: 1 }, { merge: true });
        }
      });
      
      return refNumber;
    } catch (error) {
      console.error("Error generating sequential reference number:", error);
      // Fallback format
      const fy = getFinancialYear();
      const timestamp = Date.now();
      return `CN-${fy}-${String(timestamp % 99999 + 1).padStart(5, '0')}`;
    }
  };

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setCreditNoteDate(today);
    
    // Initialize with default values from invoice configuration
    if (invoiceDefaultAdditionalNotes) {
      setAdditionalNotes(invoiceDefaultAdditionalNotes);
    }
    if (invoiceDefaultTermsAndConditions) {
      setTermsAndConditions(invoiceDefaultTermsAndConditions);
    }
    if (invoiceDefaultAuthorizedSignature) {
      setAuthorizedSignatureImage(invoiceDefaultAuthorizedSignature);
    }
    if (invoiceDefaultAuthorizedSignatureName) {
      setAuthorizedSignatureName(invoiceDefaultAuthorizedSignatureName);
    }
    
    // Generate Credit Note Number with Financial Year format
    const generateCreditNoteNumber = async () => {
      try {
        const fy = getFinancialYear();
        const userDocRef = doc(db, "users", userId);
        const userDoc = await getDoc(userDocRef);
        
        let counter = userDoc.data()?.creditNoteCounter || 0;
        counter += 1;
        
        if (counter > 99999) {
          console.error("Credit note counter exceeded maximum limit (99999)");
          setCreditNoteNumber(`CN-${fy}-00001`);
          return;
        }
        
        const creditNoteNum = `CN-${fy}-${String(counter).padStart(5, '0')}`;
        setCreditNoteNumber(creditNoteNum);
        
        // Update counter in Firebase
        await updateDoc(userDocRef, { creditNoteCounter: counter });
      } catch (error) {
        console.error("Error generating credit note number:", error);
        // Fallback to timestamp-based number
        const fy = getFinancialYear();
        setCreditNoteNumber(`CN-${fy}-00001`);
      }
    };
    
    generateCreditNoteNumber();
  }, [userId, invoiceDefaultAdditionalNotes, invoiceDefaultTermsAndConditions, invoiceDefaultAuthorizedSignature, invoiceDefaultAuthorizedSignatureName]);

  // Restore draft data when returning from product selection
  useEffect(() => {
    if (invoiceDraft && invoiceDraft.creditNoteNumber) {
      setCreditNoteNumber(invoiceDraft.creditNoteNumber);
      setReferenceNumber(invoiceDraft.referenceNumber || referenceNumber);
      setCreditNoteDate(invoiceDraft.creditNoteDate);
      setBillTo(invoiceDraft.billTo);
      setAdditionalNotes(invoiceDraft.additionalNotes || "");
      setTermsAndConditions(invoiceDraft.termsAndConditions || 'No terms and conditions specified.');
      setAuthorizedSignatureName(invoiceDraft.authorizedSignatureName || "");
      setAuthorizedSignatureImage(invoiceDraft.authorizedSignatureImage || null);
      if (invoiceDraft.creditNoteId) {
        setCreditNoteId(invoiceDraft.creditNoteId);
      }
      if (invoiceDraft.isSaved) {
        setIsSaved(invoiceDraft.isSaved);
      }
    }
  }, [invoiceDraft?.creditNoteNumber]);

  useEffect(() => {
    if (selectedItems && Array.isArray(selectedItems) && selectedItems.length > 0) {
      const newItemsFromSelection = selectedItems.map(item => {
        const quantity = parseFloat(item.moq || item.quantity || 1);
        const unitPrice = parseFloat(item.price) || 0;
        const tax = parseFloat(item.gst) || 0;
        const discount = parseFloat(item.discount) || 0;

        const discountedPrice = unitPrice * (1 - (discount / 100));
        const total = quantity * discountedPrice * (1 + (tax / 100));

        return {
          description: item.name || '',
          unitPrice,
          quantity,
          discount,
          tax,
          total,
          isLocked: true,
          sourceId: null,
        };
      });

      setItems(prevItems => {
        // Keep only items with actual data (non-empty)
        const existingValidItems = prevItems.filter(
          item => item.description?.trim() || item.quantity > 0 || item.unitPrice > 0
        );
        
        // Add new items, skipping duplicates based on description AND unitPrice combination
        const itemsToAdd = newItemsFromSelection.filter(newItem => {
          const isDuplicate = existingValidItems.some(
            existingItem => 
              existingItem.description.trim().toLowerCase() === newItem.description.trim().toLowerCase() && 
              parseFloat(existingItem.unitPrice) === parseFloat(newItem.unitPrice)
          );
          return !isDuplicate;
        });
        
        return [...existingValidItems, ...itemsToAdd];
      });

      if (setSelectedItems) {
        setSelectedItems([]);
      }
      setHasUnsavedChanges(true);
    }
  }, [selectedItems, setSelectedItems]);

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      [field]: value,
    };

    if (field === "quantity" || field === "unitPrice" || field === "discount" || field === "tax") {
      const unitPrice = parseFloat(newItems[index].unitPrice) || 0;
      const quantity = parseFloat(newItems[index].quantity) || 0;
      const discount = parseFloat(newItems[index].discount) || 0;
      const tax = parseFloat(newItems[index].tax) || 0;

      const subtotal = unitPrice * quantity;
      const discountAmount = (subtotal * discount) / 100;
      const taxableAmount = subtotal - discountAmount;
      const taxAmount = (taxableAmount * tax) / 100;
      const total = taxableAmount + taxAmount;

      newItems[index].total = parseFloat(total.toFixed(2));
    }

    setItems(newItems);
    setHasUnsavedChanges(true);
  };

  const addItem = () => {
    setItems([...items, { description: "", unitPrice: 0, quantity: 0, discount: 0, tax: 0, total: 0, isLocked: false, sourceId: null }]);
  };

  const deleteItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
      setHasUnsavedChanges(true);
    }
  };

  const resetForm = () => {
    setCreditNoteNumber("");
    setReferenceNumber("");
    setBillTo({ name: "", address: "" });
    setCreditNoteDate(new Date().toISOString().split('T')[0]);
    setItems([{ description: "", unitPrice: 0, quantity: 0, discount: 0, tax: 0, total: 0, isLocked: false, sourceId: null }]);
    setAdditionalNotes(invoiceDefaultAdditionalNotes || "");
    setTermsAndConditions(invoiceDefaultTermsAndConditions || "");
    setIsSaved(false);
    setCreditNoteId(null);
    setHasUnsavedChanges(false);
    setAuthorizedSignature("");
    setAuthorizedSignatureName(invoiceDefaultAuthorizedSignatureName || "");
    setAuthorizedSignatureImage(invoiceDefaultAuthorizedSignature || null);
    setSignatureErrors({ authorized: "" });
    setSelectedCurrency(currency || "INR");
  };

  const validateSignatureImage = (file) => {
    const validFormats = ['image/jpeg', 'image/png'];
    const maxSize = 150 * 1024; // 150KB

    if (!validFormats.includes(file.type)) {
      return "Signature must be JPEG or PNG format";
    }

    if (file.size > maxSize) {
      return "Signature must not exceed 150KB";
    }

    return "";
  };

  const handleAuthorizedSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const error = validateSignatureImage(file);
      if (error) {
        setSignatureErrors(prev => ({ ...prev, authorized: error }));
        setAuthorizedSignatureImage(null);
      } else {
        const reader = new FileReader();
        reader.onload = (event) => {
          setAuthorizedSignatureImage(event.target.result);
          setSignatureErrors(prev => ({ ...prev, authorized: "" }));
          setHasUnsavedChanges(true);
        };
        reader.readAsDataURL(file);
      }
    }
  };


  const handleSelectItems = () => {
    const currentDraft = {
      creditNoteNumber,
      referenceNumber,
      creditNoteDate,
      billTo,
      items,
      additionalNotes,
      termsAndConditions,
      authorizedSignatureName,
      authorizedSignatureImage,
      creditNoteId,
      isSaved,
    };
    if (setInvoiceDraft) {
      setInvoiceDraft(currentDraft);
    }
    navigate("/select-invoice-products", { state: { from: window.location.pathname, isDraft: true } });
  };

  const execCommand = (command) => document.execCommand(command, false, null);

  const totals = React.useMemo(() => {
    let subtotal = 0;
    let totalDiscountAmount = 0;
    let totalTaxAmount = 0;

    items.forEach(item => {
      const itemSubtotal = parseFloat(item.unitPrice || 0) * parseFloat(item.quantity || 0);
      const itemDiscount = (itemSubtotal * parseFloat(item.discount || 0)) / 100;
      const itemTax = ((itemSubtotal - itemDiscount) * parseFloat(item.tax || 0)) / 100;

      subtotal += itemSubtotal;
      totalDiscountAmount += itemDiscount;
      totalTaxAmount += itemTax;
    });

    const taxableAmount = subtotal - totalDiscountAmount;
    const grandTotal = subtotal - totalDiscountAmount + totalTaxAmount;

    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      totalDiscountAmount: parseFloat(totalDiscountAmount.toFixed(2)),
      taxableAmount: parseFloat(taxableAmount.toFixed(2)),
      totalTaxAmount: parseFloat(totalTaxAmount.toFixed(2)),
      grandTotal: parseFloat(grandTotal.toFixed(2)),
    };
  }, [items]);

  const saveCreditNote = async () => {
    if (!creditNoteNumber.trim()) {
      showNotification("Please enter a Credit Note number", 'warning');
      return;
    }

    try {
      // Generate sequential reference number only when saving (not for updates)
      let finalReferenceNumber = referenceNumber;
      if (!creditNoteId) {
        // Only generate for new credit notes
        finalReferenceNumber = await generateSequentialReferenceNumber();
        setReferenceNumber(finalReferenceNumber);
      }

      const creditNoteData = {
        creditNoteNumber,
        referenceNumber: finalReferenceNumber,
        creditNoteDate,
        billTo,
        items,
        totals,
        additionalNotes,
        termsAndConditions,
        authorizedSignatureName,
        authorizedSignatureImage,
        userId,
        currency: selectedCurrency,
        createdAt: Timestamp.now(),
        status: 'open', // Initialize as open status
      };

      if (creditNoteId) {
        const creditNoteRef = doc(db, "users", userId, "creditNotes", creditNoteId);
        await updateDoc(creditNoteRef, creditNoteData);
        showNotification("Credit Note updated successfully!", 'success');
      } else {
        const creditNotesRef = collection(db, "users", userId, "creditNotes");
        const docRef = await addDoc(creditNotesRef, creditNoteData);
        setCreditNoteId(docRef.id);
        showNotification("Credit Note saved successfully!", 'success');
      }

      setIsSaved(true);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Error saving credit note:", error);
      showNotification("Error saving credit note: " + error.message, 'error');
    }
  };

  const generatePDF = () => {
    if (!creditNoteNumber.trim()) {
      showNotification("Please enter a Credit Note number", 'warning');
      return;
    }

    generateCreditNotePDF({
      businessName: invoiceBusinessName,
      businessAddress: invoiceBusinessAddress,
      logo: invoiceLogo,
      banner: invoiceBanner,
      creditNoteNumber,
      referenceNumber,
      creditNoteDate,
      billTo,
      items,
      totals,
      currencySymbol,
      currency: selectedCurrency,
      additionalNotes,
      termsAndConditions,
      authorizedSignatureName,
      authorizedSignatureImage,
      invoiceFooter: invoiceFooter,
      isSaved,
      setIsPrinting,
      pdfFileName: creditNoteNumber ? `${creditNoteNumber}.pdf` : 'credit-note.pdf',
    });
  };

  const handleGenerateNew = () => {
    resetForm();
  };

  const handleClearForm = () => {
    resetForm();
    setIsSaved(false);
    setCreditNoteId(null);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3, maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Credit Note
          </Typography>
        </Box>

        {/* Warning for unsaved changes */}
        {hasUnsavedChanges && (
          <Box sx={{ mb: 2, p: 2, bgcolor: '#fff3cd', borderLeft: '4px solid #ff9800', display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmberIcon sx={{ color: '#ff9800' }} />
            <Typography variant="body2" sx={{ color: '#856404' }}>
              You have unsaved changes. Please save before generating PDF.
            </Typography>
          </Box>
        )}

        {/* Form Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2}>
            {/* First Row: Credit Note Number, Reference Number, Date */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Credit Note Number"
                value={creditNoteNumber}
                size="small"
                disabled
                helperText="Auto-generated"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TextField
                  fullWidth
                  label="Reference Number"
                  value={referenceNumber}
                  size="small"
                  disabled
                  placeholder="Auto-generated"
                  variant="outlined"
                  sx={{ flex: 1 }}
                />
                <Tooltip title="Reference Number can include invoice number, quotation number, or any other reference document number">
                  <InfoIcon sx={{ cursor: 'pointer', color: '#3f51b5', flexShrink: 0, fontSize: '20px', mt: 1 }} />
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Credit Note Date"
                type="date"
                value={creditNoteDate}
                onChange={(e) => { setCreditNoteDate(e.target.value); setHasUnsavedChanges(true); }}
                InputLabelProps={{ shrink: true }}
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel id="currency-select-label">Currency</InputLabel>
                <Select
                  labelId="currency-select-label"
                  id="currency-select"
                  value={selectedCurrency}
                  onChange={(e) => { setSelectedCurrency(e.target.value); setHasUnsavedChanges(true); }}
                  label="Currency"
                >
                  <MenuItem value="INR">INR - Indian Rupee</MenuItem>
                  <MenuItem value="USD">USD - US Dollar</MenuItem>
                  <MenuItem value="EUR">EUR - Euro</MenuItem>
                  <MenuItem value="GBP">GBP - British Pound</MenuItem>
                  <MenuItem value="JPY">JPY - Japanese Yen</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Second Row: Bill To Name and Address */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bill To - Name"
                value={billTo.name}
                onChange={(e) => { setBillTo({ ...billTo, name: e.target.value }); setHasUnsavedChanges(true); }}
                multiline
                rows={2}
                size="small"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    backgroundColor: '#f9f9f9',
                  },
                  '& .MuiOutlinedInput-input': {
                    lineHeight: '1.4',
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bill To - Address"
                value={billTo.address}
                onChange={(e) => { setBillTo({ ...billTo, address: e.target.value }); setHasUnsavedChanges(true); }}
                multiline
                rows={2}
                size="small"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    backgroundColor: '#f9f9f9',
                  },
                  '& .MuiOutlinedInput-input': {
                    lineHeight: '1.4',
                  }
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Items Table */}
        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Items</Typography>
        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={addItem}
          >
            + Add Item
          </Button>

          <Button 
            variant="contained" 
            color="info" 
            onClick={handleSelectItems}
          >
            Select Items from Catalog
          </Button>
        </Box>
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#3f51b5' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}><strong>Description</strong></TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}><strong>Qty</strong></TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}><strong>Unit Price</strong></TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}><strong>Discount %</strong></TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}><strong>Tax %</strong></TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}><strong>Total</strong></TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}><strong>Action</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      value={item.description}
                      onChange={(e) => handleItemChange(index, "description", e.target.value)}
                      variant="outlined"
                      sx={{ width: '100%' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                      sx={{ width: '80px' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={item.unitPrice}
                      onChange={(e) => handleItemChange(index, "unitPrice", e.target.value)}
                      sx={{ width: '100px' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={item.discount}
                      onChange={(e) => handleItemChange(index, "discount", e.target.value)}
                      sx={{ width: '80px' }}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={item.tax}
                      onChange={(e) => handleItemChange(index, "tax", e.target.value)}
                      sx={{ width: '80px' }}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(item.total, currencySymbol)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Delete Item">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => deleteItem(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Totals Section */}
        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5', display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body1"><strong>Subtotal:</strong> {formatCurrency(totals.subtotal, currencySymbol)}</Typography>
            <Typography variant="body1"><strong>Total Discount:</strong> {formatCurrency(totals.totalDiscountAmount, currencySymbol)}</Typography>
            <Typography variant="body1"><strong>Total Tax:</strong> {formatCurrency(totals.totalTaxAmount, currencySymbol)}</Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="h6"><strong>Grand Total:</strong> {formatCurrency(totals.grandTotal, currencySymbol)}</Typography>
          </Box>
        </Paper>

        {/* Additional Notes and Terms & Conditions Section */}
        <Grid container spacing={2} sx={{ mt: 2, mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="h6" sx={{ mb: 2 }}>Additional Notes</Typography>
            <Paper sx={{ p: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={additionalNotes}
                onChange={(e) => { setAdditionalNotes(e.target.value); setHasUnsavedChanges(true); }}
                variant="outlined"
              />
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="h6" sx={{ mb: 2 }}>Terms and Conditions</Typography>
            <Paper sx={{ p: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={termsAndConditions}
                onChange={(e) => { setTermsAndConditions(e.target.value); setHasUnsavedChanges(true); }}
                variant="outlined"
              />
            </Paper>
          </Grid>
        </Grid>

        {/* Signature Section */}
        <Typography variant="h6" sx={{ mt: 4, mb: 3 }}>Signatures</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: 'primary.main', fontSize: '0.95rem' }}>Authorized Signature</Typography>
              <Paper sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5, minHeight: '80px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                  {authorizedSignatureImage && authorizedSignatureImage !== "" ? (
                    <Box sx={{ mb: 1, textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                      <img 
                        src={authorizedSignatureImage} 
                        alt="Authorized Signature Preview" 
                        style={{ maxWidth: '100%', maxHeight: '60px', borderRadius: '4px' }}
                      />
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        sx={{ mt: 0.5, display: 'block', mx: 'auto' }}
                        onClick={() => {
                          setAuthorizedSignatureImage(null);
                          setSignatureErrors(prev => ({ ...prev, authorized: "" }));
                          setHasUnsavedChanges(true);
                        }}
                      >
                        Remove Signature
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 0.5 }}>
                      <CloudUploadIcon sx={{ fontSize: 28, color: '#ccc', mb: 0.5 }} />
                      <Typography variant="body2" sx={{ color: '#666', mb: 1, fontSize: '0.8rem' }}>
                        Upload Signature Image
                      </Typography>
                      <Button
                        variant="contained"
                        component="label"
                        startIcon={<CloudUploadIcon />}
                        size="small"
                      >
                        Choose Image
                        <input
                          hidden
                          accept="image/jpeg,image/png"
                          type="file"
                          onChange={handleAuthorizedSignatureUpload}
                        />
                      </Button>
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#999', fontSize: '0.7rem' }}>
                        JPEG, PNG | Max 150KB
                      </Typography>
                    </Box>
                  )}
                  {signatureErrors.authorized && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, p: 1, backgroundColor: '#ffebee', borderRadius: 0.5 }}>
                      <WarningAmberIcon sx={{ color: '#d32f2f', fontSize: 20 }} />
                      <Typography variant="caption" sx={{ color: '#d32f2f' }}>
                        {signatureErrors.authorized}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>
          </Grid>
        </Grid>

        {/* Action Buttons in the Middle */}
        <Grid container spacing={2} sx={{ mt: 4, mb: 8, position: 'relative', zIndex: 10 }} justifyContent="center">
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="contained" 
              color="primary" 
              fullWidth 
              onClick={saveCreditNote}
              sx={{ borderRadius: 2, padding: "10px 0" }}
              disabled={!hasUnsavedChanges && isSaved}
            >
              {isSaved ? 'Save Changes' : 'Save Credit Note'}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              onClick={generatePDF}
              sx={{ borderRadius: 2, padding: "10px 0" }}
              disabled={!isSaved || isPrinting}
            >
              {isPrinting ? 'Generating...' : 'Download PDF'}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="outlined" 
              color="primary" 
              fullWidth 
              onClick={handleGenerateNew}
              startIcon={<RefreshIcon />}
              sx={{ borderRadius: 2, padding: "10px 0" }}
            >
              Generate New
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="outlined" 
              color="secondary" 
              fullWidth 
              onClick={handleClearForm}
              startIcon={<ClearAllIcon />}
              sx={{ borderRadius: 2, padding: "10px 0" }}
            >
              Clear Form
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={2500}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default CreditNote;
